import {
  Divider,
  VStack,
  Flex,
  HStack,
  Spacer,
  Stack,
} from '@chakra-ui/layout';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Button,
  Tag,
  TagLabel,
  DrawerFooter,
  Skeleton,
  TagLeftIcon,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  error as commonError,
  finleyAssert,
} from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import {
  ResolvedLoanFragmentGQLType,
  useGetCreditFacilityMetricsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { TaskDetails } from 'packages/tasks/ui/TaskEntityDetails';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { MarkLenderShareComponentFundedButton } from '~/packages/loans/ui/components/FundTransfer';
import { DownloadLiveFundingNoticeButton } from '~/packages/loans/ui/DownloadLiveFundingNoticeButton';
import { FundingRequestDetails } from '~/packages/loans/ui/FundingRequestDrawer';

export const LenderSharesDrawer = ({
  isOpen,
  loan,
  lenderShareId,
  fundingComponentId,
  facilityMechanics,
  onToggle,
  taskId,
}: {
  isOpen: boolean;
  fundingComponentId: string;
  loan: ResolvedLoanFragmentGQLType;
  lenderShareId: string;
  facilityMechanics: CreditAgreement.DomainEntity.FacilityMechanicsWithEntities;
  onToggle: (isOpen: boolean) => void;
  taskId?: string;
}) => {
  const lenderShare = loan.funding.lenderShares.find(
    (ls) => ls.lenderShareId === lenderShareId,
  );
  const fundingRequests = loan.funding.requests.filter((request) =>
    request.fundingComponents.some(
      (fc) => fc.fundingRequestComponentId === fundingComponentId,
    ),
  );
  finleyAssert(
    fundingRequests.length === 1,
    new commonError.FinleyError(
      `Expected only one funding request associated with [fundingComponentId:${fundingComponentId}] but found ${fundingRequests.length}.`,
      {
        metadata: {
          logMeta: {
            fundingRequestIds: fundingRequests.map((fr) => fr.fundingRequestId),
          },
        },
      },
    ),
  );

  const fundingRequest = fundingRequests[0];

  const fundingComponent = loan.funding.requests
    .flatMap((request) => request.fundingComponents)
    .find((fc) => fc.fundingRequestComponentId === fundingComponentId);

  if (!lenderShare || !fundingComponent) {
    throw new commonError.FinleyError(
      '[Task Center] Cannot find lender share for loan. Contact Finley Support.',
      {
        metadata: {
          logMeta: {
            lenderShareId,
            loanId: loan.id,
          },
        },
      },
    );
  }

  const lenderCommitmentAmount = useMemo(() => {
    const capitalProviderEntity = facilityMechanics.parties.capitalProviders.find(
      (capProvider) =>
        capProvider.entity.id === lenderShare.lenderOrganization.id,
    );

    finleyAssert(
      capitalProviderEntity,
      `[LenderSharesDrawer] Missing capitalProviderEntity for ${_.startCase(
        lenderShare.lenderOrganization.name,
      )}`,
    );

    return capitalProviderEntity.commitmentAmount;
  }, [
    facilityMechanics.parties.capitalProviders,
    lenderShare.lenderOrganization.id,
    lenderShare.lenderOrganization.name,
  ]);

  const { data: metricsDataRequest } = useGetCreditFacilityMetricsQuery({
    skip: !loan?.facility,
    variables: {
      creditFacilityId: loan?.facility.creditFacilityId as string,
      postingDate: commonDate.currentDateStr(),
    },
    fetchPolicy: 'no-cache',
  });

  const metrics = metricsDataRequest?.metrics;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => onToggle(false)}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt="1.5rem" px="3rem" pb="0">
          <Typography variant="section-title" trim>
            Funding Notice for{' '}
            {_.startCase(lenderShare.lenderOrganization.name)}
          </Typography>
        </DrawerHeader>
        <Divider my="1.5rem" w="100%" />
        <DrawerBody px="3rem">
          <VStack align="flex-start" gap="2rem">
            <HStack justifyContent="space-between" w="100%">
              <DownloadLiveFundingNoticeButton
                lenderShareId={lenderShare.lenderShareId}
                fundingRequestComponentId={fundingComponentId}
              />
              <Spacer />
              {(() => {
                // Can only mark as funded if loan is in "initiated" or "funded" or "overdue" state.
                // For "initiated" (per product), the admin agent may collect money from
                // their participants prior to borrower actually receiving the ACH transfer
                if (
                  loan.calculatedLoanStatus !== 'initiated' &&
                  loan.calculatedLoanStatus !== 'funded' &&
                  loan.calculatedLoanStatus !== 'overdue'
                ) {
                  return <Typography>-</Typography>;
                }

                if (fundingComponent.fundTransferDate) {
                  return (
                    <Tag variant="solid-muted" colorScheme="green">
                      <TagLeftIcon>
                        <Icon icon="circle-check" />
                      </TagLeftIcon>
                      <TagLabel>Paid</TagLabel>
                    </Tag>
                  );
                }

                return (
                  <MarkLenderShareComponentFundedButton
                    fundingRequest={fundingRequest}
                    fundingComponent={fundingComponent}
                    resolvedLoan={loan}
                  />
                );
              })()}
            </HStack>

            {taskId && <TaskDetails taskId={taskId} />}

            <Divider />
            <Typography variant="section-subtitle" trim>
              {_.startCase(lenderShare.lenderOrganization.name)} Funding Share
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">
                  Lender Funding Share
                </Typography>
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    lenderShare.initialFundingAmount,
                    Bank.Currency.USD,
                  )}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Lender Funded Date</Typography>
                <Typography variant="base-muted">
                  {fundingComponent.fundTransferDate
                    ? commonDate.fullDateStrToHumanDateString(
                        fundingComponent.fundTransferDate,
                      )
                    : '-'}
                </Typography>
              </HStack>
            </VStack>

            <Divider />
            <Typography variant="section-subtitle" trim>
              Facility Details
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Name</Typography>
                <Typography variant="base-muted">
                  {_.startCase(loan.facility.creditFacilityName)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Borrower</Typography>
                <Typography variant="base-muted">
                  {_.startCase(loan.parties.borrowerOrganization.name)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Global Commitment</Typography>
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    loan.facility.commitmentAmount,
                    Bank.Currency.USD,
                  )}
                </Typography>
              </HStack>
              <HStack alignItems="start" w="100%">
                <Typography fontWeight="medium">
                  {_.startCase(lenderShare.lenderOrganization.name)} Commitment
                </Typography>

                <Spacer />
                <Stack alignItems="end">
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      lenderCommitmentAmount.value,
                      lenderCommitmentAmount.currency,
                    )}
                  </Typography>
                  <Typography variant="base-muted">
                    (
                    {percentageToPercentageLabel(
                      (lenderCommitmentAmount.value /
                        loan.facility.commitmentAmount) *
                        100,
                      6,
                    )}{' '}
                    Pro Rata)
                  </Typography>
                </Stack>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Global Principal</Typography>
                {metrics ? (
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      metrics.outstandingPrincipalBalance,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                ) : (
                  <Skeleton w="10rem" h="1rem" />
                )}
              </HStack>
            </VStack>

            <Divider />
            <Typography variant="section-subtitle" trim>
              Funding Request
            </Typography>
            <FundingRequestDetails
              key={fundingRequest.fundingRequestId}
              fundingRequest={fundingRequest}
              loan={loan}
            />
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <Flex justify="flex-end" w="100%" mb="2em">
            <Button
              as={RouterLink}
              to={`/loans/${loan.id}`}
              rightIcon={<Icon icon="chevron-right" />}
              variant="v2-ghost"
            >
              View Loan
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
